import React, { useState } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { CallbackModal } from '../components/CallbackModal';
import { MapPin, Star, Scan, Languages, Calendar, MessageCircle, Phone, CheckCircle2, ArrowRight, Plus, Minus, ShieldCheck, CreditCard, Clock, UserCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
const TELEGRAM_URL = 'https://t.me/dentico_clinic';
const MAPS_URL = 'https://maps.app.goo.gl/Fhof2j1jnLA3ffqT6';
const MAPS_EMBED_URL = 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1241.0181846982155!2d-0.104187760804084!3d51.53089276153758!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48760f7c6534e077%3A0xa54a590bd3b2a149!2sDentico%20Clinic%20-%20Private%20Dentist%20in%20Angel%2C%20Islington!5e0!3m2!1suk!2suk!4v1769722166469!5m2!1suk!2suk';
function TelegramIcon({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" fill="currentColor" className={className} data-id="element-1717">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" data-id="element-1718" />
    </svg>;
}
function AccordionItem({
  question,
  answer,
  isOpen,
  onToggle
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return <div className="border-b border-gray-100 last:border-b-0" data-id="element-1719">
      <button onClick={onToggle} className="w-full flex items-center justify-between py-4 text-left focus:outline-none group" data-id="element-1720">
        <span className="text-lg font-medium text-gray-900 group-hover:text-teal-600 transition-colors" data-id="element-1721">
          {question}
        </span>
        <span className={`flex-shrink-0 ml-4 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-400'}`} data-id="element-1722">
          {isOpen ? <Minus className="w-4 h-4" data-id="element-1723" /> : <Plus className="w-4 h-4" data-id="element-1724" />}
        </span>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100 pb-4' : 'max-h-0 opacity-0'}`} data-id="element-1725">
        <p className="text-gray-600 leading-relaxed" data-id="element-1726">{answer}</p>
      </div>
    </div>;
}
export function ImplantyLondonPage() {
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  const faqs = [{
    q: 'Скільки коштує імплантація зуба в Лондоні?',
    a: 'Вартість імплантації залежить від клінічної ситуації, типу імпланта та необхідності додаткових процедур (наприклад, кісткова пластика). Після консультації ми складаємо прозорий план лікування та кошторис.'
  }, {
    q: 'Чи боляче ставити імплант?',
    a: 'Процедура проводиться під місцевою анестезією. Під час встановлення імпланта більшість пацієнтів не відчувають болю, а після — можливий дискомфорт, який контролюється рекомендаціями та знеболенням.'
  }, {
    q: 'Скільки часу займає імплантація?',
    a: 'Терміни залежать від випадку. Зазвичай спочатку проводиться консультація та діагностика, потім встановлення імпланта, а після періоду загоєння — встановлення коронки. Точний графік визначається індивідуально.'
  }, {
    q: 'Чи підходить імплантація, якщо давно немає зуба?',
    a: "Так, але може знадобитися оцінка стану кістки. Під час консультації ми перевіряємо об'єм кістки та за потреби обговорюємо варіанти відновлення."
  }, {
    q: 'Чи можна поставити імплант за один візит?',
    a: 'Іноді можливе одномоментне встановлення (immediate loading), але частіше потрібен період загоєння для інтеграції імпланта. Це визначається після ретельної діагностики.'
  }, {
    q: 'Які імпланти ви використовуєте?',
    a: 'Ми працюємо з перевіреними системами імплантів преміум-класу, які мають довгострокову гарантію та високий відсоток приживлення.'
  }, {
    q: 'Чи можна отримати консультацію українською?',
    a: 'Так, у Dentico Clinic можливе спілкування українською мовою під час консультації та лікування.'
  }, {
    q: 'Де знаходиться клініка?',
    a: 'Dentico Clinic розташована в Angel / Islington, Central London (EC1V). Адреса: 360–364 City Road, London EC1V 2PY, поруч з Angel Station.'
  }];
  // Schemas
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a
      }
    }))
  };
  const procedureSchema = {
    '@context': 'https://schema.org',
    '@type': 'MedicalProcedure',
    name: 'Dental Implants',
    description: 'Surgical placement of dental implants to replace missing teeth.',
    procedureType: 'http://schema.org/SurgicalProcedure',
    provider: {
      '@type': 'Dentist',
      name: 'Dentico Clinic',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '360-364 City Road',
        addressLocality: 'London',
        postalCode: 'EC1V 2PY',
        addressCountry: 'GB'
      }
    }
  };
  return <Layout data-id="element-1727">
      <CallbackModal isOpen={isCallbackOpen} onClose={() => setIsCallbackOpen(false)} data-id="element-1728" />

      {/* Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} data-id="element-1729" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(procedureSchema)
    }} data-id="element-1730" />

      {/* 1. HERO SECTION */}
      <section className="relative bg-white overflow-hidden" data-id="element-1731">
        <div className="absolute inset-0 bg-teal-50/50 -skew-y-3 origin-top-left transform translate-y-20" data-id="element-1732" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 lg:pt-20 lg:pb-24 relative" data-id="element-1733">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-1734">
            <div className="space-y-6" data-id="element-1735">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-100 text-teal-800 text-sm font-medium" data-id="element-1736">
                <span className="relative flex h-2 w-2" data-id="element-1737">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75" data-id="element-1738"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-500" data-id="element-1739"></span>
                </span>
                Імплантація зубів у Лондоні
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight tracking-tight" data-id="element-1740">
                Імплантація зубів у Лондоні –{' '}
                <span className="text-teal-600" data-id="element-1741">Dentico Clinic</span>
                <br data-id="element-1742" />
                <span className="text-2xl sm:text-3xl font-medium text-gray-500 mt-2 block" data-id="element-1743">
                  (Angel, Islington)
                </span>
              </h1>

              <p className="text-lg text-gray-600 max-w-lg leading-relaxed" data-id="element-1744">
                Відновлення втрачених зубів за допомогою сучасних імплантів.
                Консультація українською мовою, цифрова діагностика та прозорий
                план лікування.
              </p>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-3 text-sm font-medium text-gray-700" data-id="element-1745">
                <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm" data-id="element-1746">
                  <Star className="w-4 h-4 text-yellow-400 fill-current" data-id="element-1747" />
                  5★ Google Reviews
                </div>
                <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm" data-id="element-1748">
                  <MapPin className="w-4 h-4 text-teal-600" data-id="element-1749" />
                  Angel, EC1V
                </div>
                <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm" data-id="element-1750">
                  <Languages className="w-4 h-4 text-teal-600" data-id="element-1751" />
                  Українська мова
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4" data-id="element-1752">
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-1753">
                  <Button size="lg" className="w-full gap-2 shadow-lg shadow-teal-600/20" data-id="element-1754">
                    <Calendar className="w-5 h-5" data-id="element-1755" />
                    Записатись на консультацію
                  </Button>
                </a>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-1756">
                  <Button variant="outline" size="lg" className="w-full gap-2 border-teal-200 text-teal-700 hover:bg-teal-50" data-id="element-1757">
                    <MessageCircle className="w-5 h-5 text-green-600" data-id="element-1758" />
                    WhatsApp
                  </Button>
                </a>
              </div>
            </div>

            <div className="relative" data-id="element-1759">
              <div className="absolute inset-0 bg-teal-200 rounded-3xl transform rotate-3 blur-sm opacity-30" data-id="element-1760"></div>
              <img src="https://images.unsplash.com/photo-1606811841689-23dfddce3e95?auto=format&fit=crop&w=1200&q=80" alt="Імплантація зубів - Dentico Clinic London" className="relative rounded-3xl shadow-2xl object-cover h-[300px] sm:h-[400px] lg:h-[500px] w-full" data-id="element-1761" />
            </div>
          </div>
        </div>
      </section>

      {/* 2. WHAT ARE IMPLANTS */}
      <section className="py-16 bg-white" data-id="element-1762">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1763">
          <div className="text-center mb-10" data-id="element-1764">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-1765">
              Що таке дентальна імплантація?
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed" data-id="element-1766">
              Дентальна імплантація — це найсучасніший метод відновлення
              втрачених зубів, який дозволяє повернути не лише естетику
              посмішки, але й повну жувальну функцію. Імплант — це титановий
              корінь, який встановлюється в кістку щелепи і служить надійною
              опорою для коронки або мостоподібного протеза.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mt-4" data-id="element-1767">
              На відміну від знімних протезів, імпланти відчуваються і
              функціонують як власні зуби. Вони запобігають атрофії кісткової
              тканини і не вимагають обточування сусідніх здорових зубів, що
              робить їх найкращим довгостроковим рішенням для відновлення
              зубного ряду.
            </p>
          </div>
        </div>
      </section>

      {/* 3. PROCESS */}
      <section className="py-16 bg-gray-50" data-id="element-1768">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1769">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center" data-id="element-1770">
            Етапи імплантації в Dentico Clinic
          </h2>
          <div className="grid md:grid-cols-4 gap-8" data-id="element-1771">
            {[{
            step: '01',
            title: 'Консультація',
            desc: 'Огляд, 3D-діагностика (КТ) та складання детального плану лікування.'
          }, {
            step: '02',
            title: 'Встановлення',
            desc: 'Хірургічний етап встановлення імпланта під місцевою анестезією.'
          }, {
            step: '03',
            title: 'Загоєння',
            desc: 'Період остеоінтеграції (приживлення), який триває від 3 до 6 місяців.'
          }, {
            step: '04',
            title: 'Протезування',
            desc: 'Встановлення постійної керамічної коронки на імплант.'
          }].map((item, idx) => <Card key={idx} className="p-6 relative overflow-hidden" data-id="element-1772">
                <div className="text-6xl font-bold text-teal-50 absolute -top-4 -right-4 select-none" data-id="element-1773">
                  {item.step}
                </div>
                <div className="relative z-10" data-id="element-1774">
                  <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1775">
                    {item.title}
                  </h3>
                  <p className="text-gray-600" data-id="element-1776">{item.desc}</p>
                </div>
              </Card>)}
          </div>
        </div>
      </section>

      {/* 4. WHO NEEDS IMPLANTS */}
      <section className="py-16 bg-white" data-id="element-1777">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1778">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-1779">
            <div data-id="element-1780">
              <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-1781">
                Кому підходить імплантація?
              </h2>
              <ul className="space-y-4" data-id="element-1782">
                {['Відсутність одного або декількох зубів', 'Бажання відмовитися від знімних протезів', 'Руйнування мостоподібних конструкцій', 'Необхідність відновлення жувальної функції', 'Естетичні дефекти зубного ряду'].map((item, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-1783">
                    <CheckCircle2 className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" data-id="element-1784" />
                    <span className="text-gray-700 text-lg" data-id="element-1785">{item}</span>
                  </li>)}
              </ul>
              <div className="mt-8" data-id="element-1786">
                <Link to="/ua/contact" data-id="element-1787">
                  <Button size="lg" className="gap-2" data-id="element-1788">
                    <Calendar className="w-5 h-5" data-id="element-1789" />
                    Записатись на консультацію
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative h-[400px] rounded-2xl overflow-hidden shadow-xl" data-id="element-1790">
              <img src="https://images.unsplash.com/photo-1609840114035-3c981b782dfe?auto=format&fit=crop&w=1200&q=80" alt="Дентальна імплантація процес" className="absolute inset-0 w-full h-full object-cover" data-id="element-1791" />
            </div>
          </div>
        </div>
      </section>

      {/* 5. BENEFITS */}
      <section className="py-16 bg-teal-50" data-id="element-1792">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1793">
          <div className="text-center mb-12" data-id="element-1794">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-1795">
              Переваги імплантації в Dentico Clinic
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-1796">
              Ми використовуємо передові технології та матеріали для найкращого
              результату.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1797">
            {[{
            icon: Scan,
            title: 'Цифрове планування',
            desc: 'Точне 3D-моделювання операції для передбачуваного результату.'
          }, {
            icon: UserCheck,
            title: 'Досвідчена команда',
            desc: 'Лікарі з багаторічним досвідом у хірургічній стоматології.'
          }, {
            icon: Languages,
            title: 'Українська мова',
            desc: 'Повне розуміння та комфортна комунікація рідною мовою.'
          }, {
            icon: ShieldCheck,
            title: 'Гарантія якості',
            desc: 'Використовуємо лише сертифіковані імпланти світових брендів.'
          }, {
            icon: Clock,
            title: 'Економія часу',
            desc: 'Оптимізовані протоколи лікування для вашого комфорту.'
          }, {
            icon: MapPin,
            title: 'Зручна локація',
            desc: 'Центр Лондона, 5 хвилин від станції метро Angel.'
          }].map((item, idx) => <Card key={idx} className="p-6 bg-white" data-id="element-1798">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center text-teal-600 mb-4" data-id="element-1799">
                  <item.icon className="w-6 h-6" data-id="element-1800" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-1801">
                  {item.title}
                </h3>
                <p className="text-gray-600" data-id="element-1802">{item.desc}</p>
              </Card>)}
          </div>
        </div>
      </section>

      {/* MID-PAGE CTA */}
      <section className="py-12 bg-teal-600" data-id="element-1803">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-1804">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4" data-id="element-1805">
            Дізнайтесь, чи підходить вам імплантація
          </h2>
          <p className="text-teal-100 mb-6" data-id="element-1806">
            Запишіться на консультацію — ми складемо індивідуальний план
            лікування.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3" data-id="element-1807">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-id="element-1808">
              <Button size="lg" className="gap-2 bg-white text-teal-900 hover:bg-teal-50" data-id="element-1809">
                <Calendar className="w-5 h-5" data-id="element-1810" />
                Записатись онлайн
              </Button>
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-id="element-1811">
              <Button variant="outline" size="lg" className="gap-2 border-white text-white hover:bg-white/10" data-id="element-1812">
                <MessageCircle className="w-5 h-5" data-id="element-1813" />
                WhatsApp
              </Button>
            </a>
            <Button variant="outline" size="lg" className="gap-2 border-white text-white hover:bg-white/10" onClick={() => setIsCallbackOpen(true)} data-id="element-1814">
              <Phone className="w-5 h-5" data-id="element-1815" />
              Замовити дзвінок
            </Button>
          </div>
        </div>
      </section>

      {/* 6. FAQ */}
      <section className="py-16 bg-white" data-id="element-1816">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1817">
          <div className="text-center mb-10" data-id="element-1818">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-1819">
              Часті запитання про імплантацію
            </h2>
          </div>
          <div className="space-y-2" data-id="element-1820">
            {faqs.map((faq, idx) => <AccordionItem key={idx} question={faq.q} answer={faq.a} isOpen={openFaqIndex === idx} onToggle={() => toggleFaq(idx)} data-id="element-1821" />)}
          </div>
        </div>
      </section>

      {/* 7. INTERNAL LINKS */}
      <section className="py-12 bg-gray-50 border-t border-gray-100" data-id="element-1822">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1823">
          <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1824">
            Дивіться також
          </h2>
          <div className="flex flex-wrap gap-4" data-id="element-1825">
            <Link to="/ua/ukrainskyi-stomatolog-london" data-id="element-1826">
              <Button variant="outline" size="sm" data-id="element-1827">
                Український стоматолог
              </Button>
            </Link>
            <Link to="/ua/veneers-london" data-id="element-1828">
              <Button variant="outline" size="sm" data-id="element-1829">
                Вініри
              </Button>
            </Link>
            <Link to="/ua/root-canal-london" data-id="element-1830">
              <Button variant="outline" size="sm" data-id="element-1831">
                Лікування каналів
              </Button>
            </Link>
            <Link to="/ua/emergency-dentist-london" data-id="element-1832">
              <Button variant="outline" size="sm" data-id="element-1833">
                Невідкладна допомога
              </Button>
            </Link>
            <Link to="/ua/services/crowns" data-id="element-1834">
              <Button variant="outline" size="sm" data-id="element-1835">
                Коронки
              </Button>
            </Link>
            <Link to="/ua/prices" data-id="element-1836">
              <Button variant="outline" size="sm" data-id="element-1837">
                Ціни
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 8. CTA */}
      <section className="py-16 bg-teal-900" data-id="element-1838">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-1839">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" data-id="element-1840">
            Поверніть собі впевнену посмішку
          </h2>
          <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto" data-id="element-1841">
            Запишіться на консультацію з імплантації сьогодні.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-1842">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-1843">
              <Button size="lg" className="w-full gap-2 bg-white text-teal-900 hover:bg-teal-50" data-id="element-1844">
                <Calendar className="w-5 h-5" data-id="element-1845" />
                Записатись онлайн
              </Button>
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-1846">
              <Button variant="outline" size="lg" className="w-full gap-2 border-teal-400 text-teal-100 hover:bg-teal-800 hover:text-white" data-id="element-1847">
                <MessageCircle className="w-5 h-5" data-id="element-1848" />
                WhatsApp
              </Button>
            </a>
            <div className="w-full sm:w-auto" data-id="element-1849">
              <Button variant="outline" size="lg" className="w-full gap-2 border-teal-400 text-teal-100 hover:bg-teal-800 hover:text-white" onClick={() => setIsCallbackOpen(true)} data-id="element-1850">
                <Phone className="w-5 h-5" data-id="element-1851" />
                Замовити дзвінок
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>;
}